import { format, formatDistanceToNowStrict, isBefore, isThisYear, isToday, isTomorrow, isYesterday, startOfDay} from 'date-fns'
import { hi as hiLocale } from 'date-fns/locale/hi'
import { useUiStore } from '@/stores'
import { tt } from '@/internationalization/i18n'

/**
 * Date/time strings follow the interface language. The formatters are pure
 * functions called from many render paths, so they read `uiLanguage` from the
 * store directly (via `tt`) rather than taking it as an argument — non-reactive
 * is correct here because a language switch is followed by navigation that
 * re-mounts these call sites, and the visible screen at the moment of the
 * switch (Settings) shows no dates.
 */
function dfnsLocale() {
  return useUiStore.getState().uiLanguage === 'hi' ? { locale: hiLocale } : undefined
}


/** 4501 -> "1:15:01", 91 -> "1:31". Used for every media timestamp. */
export function formatTimecode(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(s / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  const seconds = s % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

/**
 * Media position, or an em dash when there is none.
 *
 * A model-extracted decision or action item can carry no position at all, and
 * rendering that as `0:00` claims it was said in the first second. `—` is the
 * house convention for an absent measurement (STYLE-GUIDE §10).
 */
export function formatTimecodeOrDash(totalSeconds: number | null | undefined): string {
  return totalSeconds == null ? '—' : formatTimecode(totalSeconds)
}

/**
 * A platform meeting id the way the platform itself prints it, so it can be
 * checked against an invite by eye: Teams digits in groups of three
 * ("412 345 678 90"), a Meet code unchanged. The API stores Teams ids unspaced.
 */
export function formatPlatformMeetingId(id: string): string {
  return /^\d+$/.test(id) ? id.replace(/(\d{3})(?=\d)/g, '$1 ') : id
}

/** 5400 -> "1h 30m", 480 -> "8m", 45 -> "45s". For durations in prose. */
export function formatDuration(totalSeconds: number | null): string {
  if (totalSeconds == null) return '—'
  if (totalSeconds < 60) return `${Math.round(totalSeconds)}s`
  const minutes = Math.round(totalSeconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`
}

/**
 * Is this deadline in the past? Compared by DAY, never by instant.
 *
 * A due date is a day: the API sends it as midnight UTC because the column is
 * a `date`, so `new Date(dueDate) < new Date()` — the obvious comparison, and
 * the one that was here — makes an item due TODAY read as "Overdue" from the
 * moment the day begins. That is the one label a person acts on immediately,
 * so it has to be right on the day it matters most.
 */
export function isOverdueDay(iso: string | null | undefined): boolean {
  if (!iso) return false
  return isBefore(startOfDay(new Date(iso)), startOfDay(new Date()))
}

/** Calendar-relative day label: "Today", "Yesterday", "Tue 12 Aug". */
export function formatDayLabel(iso: string): string {
  const d = new Date(iso)
  if (isToday(d)) return tt('time.today')
  if (isYesterday(d)) return tt('time.yesterday')
  if (isTomorrow(d)) return tt('time.tomorrow')
  return format(d, isThisYear(d) ? 'EEE d MMM' : 'EEE d MMM yyyy', dfnsLocale())
}

export function formatTime(iso: string): string {
  return format(new Date(iso), 'HH:mm', dfnsLocale())
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return format(d, isThisYear(d) ? 'd MMM, HH:mm' : 'd MMM yyyy, HH:mm', dfnsLocale())
}

/** "3 min ago". Strict so it never says "about". */
export function formatRelative(iso: string): string {
  return tt('time.ago', { value: formatDistanceToNowStrict(new Date(iso), dfnsLocale()) })
}

export function formatPercent(fraction: number, digits = 0): string {
  return `${(fraction * 100).toFixed(digits)}%`
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-IN').format(n)
}

/** 74_500 minutes -> "1,241h". Billing screens quote hours, not minutes. */
export function formatMinutesAsHours(minutes: number): string {
  return `${formatNumber(Math.round(minutes / 60))}h`
}

/** "Varun Pandey" -> "VP". Never more than two characters. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
